import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import ContentLoader, { Rect } from 'react-content-loader/native';
import { useTheme } from '@/context/ThemeContext';
import { BORDER_RADIUS } from '@/constants/config';

const createStyles = (colors) =>
  StyleSheet.create({
    container: {
      width: '100%',
      gap: 16,
    },
    heroSkeleton: {
      paddingBottom: 16,
      gap: 12,
    },
    statsRow: {
      flexDirection: 'row',
      gap: 10,
      marginTop: 4,
    },
    controlsCard: {
      padding: 16,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
    },
    searchRow: {
      marginVertical: 4,
    },
    listWrapper: {
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: BORDER_RADIUS.large,
      overflow: 'hidden',
      backgroundColor: colors.solid_color_block,
    },
    item: {
      paddingVertical: 16,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      gap: 10,
    },
  });

function SkeletonItem({ colors }) {
  return (
    <View
      style={{
        paddingVertical: 16,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderColor: colors.border_color_secondary,
        backgroundColor: colors.solid_color_block,
        gap: 10,
      }}
    >
      <ContentLoader
        speed={1.4}
        width="100%"
        height={44}
        backgroundColor={colors.solid_color_tertiary}
        foregroundColor={colors.border_color_secondary}
      >
        <Rect x="0" y="0" rx="4" ry="4" width="88%" height="13" />
        <Rect x="0" y="20" rx="4" ry="4" width="62%" height="13" />
      </ContentLoader>
      <ContentLoader
        speed={1.4}
        width="100%"
        height={18}
        backgroundColor={colors.solid_color_tertiary}
        foregroundColor={colors.border_color_secondary}
      >
        <Rect x="0" y="0" rx="8" ry="8" width="95" height="18" />
        <Rect x="105" y="3" rx="3" ry="3" width="75" height="12" />
      </ContentLoader>
    </View>
  );
}

export default function MemoryListSkeleton() {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  return (
    <View style={styles.container}>
      {/* Hero Skeleton */}
      <View style={styles.heroSkeleton}>
        <ContentLoader
          speed={1.4}
          width="100%"
          height={68}
          backgroundColor={colors.solid_color_tertiary}
          foregroundColor={colors.border_color_secondary}
        >
          <Rect x="0" y="0" rx="10" ry="10" width="140" height="20" />
          <Rect x="0" y="28" rx="6" ry="6" width="220" height="28" />
          <Rect x="0" y="62" rx="4" ry="4" width="460" height="14" />
        </ContentLoader>

        {/* Stats Row Skeleton */}
        <View style={styles.statsRow}>
          {[1, 2, 3, 4].map((i) => (
            <View
              key={i}
              style={{
                flex: 1,
                height: 54,
                borderRadius: BORDER_RADIUS.medium,
                backgroundColor: colors.solid_color_block,
                borderWidth: 1,
                borderColor: colors.border_color_secondary,
                padding: 8,
              }}
            >
              <ContentLoader
                speed={1.4}
                width="100%"
                height={36}
                backgroundColor={colors.solid_color_tertiary}
                foregroundColor={colors.border_color_secondary}
              >
                <Rect x="0" y="2" rx="4" ry="4" width="40" height="18" />
                <Rect x="0" y="24" rx="3" ry="3" width="70" height="10" />
              </ContentLoader>
            </View>
          ))}
        </View>
      </View>

      {/* Controls Card Skeleton */}
      <View style={styles.controlsCard}>
        <ContentLoader
          speed={1.4}
          width="100%"
          height={32}
          backgroundColor={colors.solid_color_tertiary}
          foregroundColor={colors.border_color_secondary}
        >
          <Rect x="0" y="4" rx="4" ry="4" width="180" height="16" />
          <Rect x="0" y="24" rx="3" ry="3" width="300" height="12" />
        </ContentLoader>
      </View>

      {/* Search & Tabs Skeleton */}
      <View style={styles.searchRow}>
        <ContentLoader
          speed={1.4}
          width="100%"
          height={42}
          backgroundColor={colors.solid_color_tertiary}
          foregroundColor={colors.border_color_secondary}
        >
          <Rect x="0" y="0" rx="8" ry="8" width="100%" height="42" />
        </ContentLoader>
      </View>

      {/* List skeleton */}
      <View style={styles.listWrapper}>
        <SkeletonItem colors={colors} />
        <SkeletonItem colors={colors} />
        <SkeletonItem colors={colors} />
        <SkeletonItem colors={colors} />
      </View>
    </View>
  );
}
